import { useEffect, useMemo, useRef, useState, type FocusEventHandler, type KeyboardEventHandler } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Grid, TextField, CircularProgress, FormHelperText, Box, Autocomplete, InputAdornment,
} from '@mui/material';
import PhoneNumberField from './PhoneNumberField';
import {
  FALLBACK_COUNTRY_DIAL_CODES,
  listCountryDialCodes,
  type CountryDialOption,
} from '../../../services/countryDialCodeService';
import { localPhoneDigits, splitPhoneByDialCodes } from '../../../utils/phoneDialCode';
import { parsePhoneLength } from '../../../utils/personFieldValidation';

interface Props {
  label: string;
  phone: string;
  countryCode: string;
  onChange: (next: { phone: string; countryCode: string }) => void;
  preferredCountry?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  /** Country code sits beside the number instead of stacking above it. */
  layout?: 'stacked' | 'inline';
  hideHelper?: boolean;
  disabled?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

function optionLabel(o: CountryDialOption): string {
  return `${o.dialCode} ${o.code || o.name}`.trim();
}

export default function PhoneWithDialCodeField({
  label,
  phone,
  countryCode,
  onChange,
  preferredCountry = 'IN',
  error,
  helperText,
  required,
  layout = 'stacked',
  hideHelper,
  disabled,
  onBlur,
  onKeyDown,
}: Props) {
  const { data: remoteOptions = [], isLoading } = useQuery({
    queryKey: ['country-dial-codes'],
    queryFn: listCountryDialCodes,
    staleTime: 5 * 60_000,
  });

  const options = remoteOptions.length ? remoteOptions : FALLBACK_COUNTRY_DIAL_CODES;
  const dialCodes = useMemo(() => options.map((o) => o.dialCode), [options]);
  const [codeQuery, setCodeQuery] = useState(countryCode || '');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!options.length) return;

    let nextCode = countryCode;
    let nextPhone = localPhoneDigits(phone);

    if (phone.startsWith('+')) {
      const split = splitPhoneByDialCodes(phone, dialCodes);
      if (split.dialCode) {
        nextCode = split.dialCode;
        nextPhone = split.localNumber;
      }
    }

    if (!nextCode && !String(phone).startsWith('+')) {
      const preferred = preferredCountry
        ? options.find((o) => o.code === preferredCountry.toUpperCase())
        : undefined;
      const fallback = preferred || options.find((o) => o.code === 'IN') || options[0];
      if (fallback) nextCode = fallback.dialCode;
    }

    if (nextCode === countryCode && nextPhone === localPhoneDigits(phone) && !phone.startsWith('+')) return;
    onChangeRef.current({ countryCode: nextCode, phone: nextPhone });
  }, [options, dialCodes, phone, countryCode, preferredCountry]);

  useEffect(() => {
    if (countryCode) setCodeQuery(countryCode);
  }, [countryCode]);

  const selected = options.find((o) => o.dialCode === countryCode)
    || (!countryCode && preferredCountry
      ? options.find((o) => o.code === preferredCountry.toUpperCase())
      : undefined)
    || (countryCode
      ? { code: '', name: countryCode, dialCode: countryCode }
      : (options.find((o) => o.code === 'IN') || options[0]))
    || null;
  const length = parsePhoneLength(selected?.phoneLength)
    || (selected?.dialCode
      ? (() => {
          const fb = FALLBACK_COUNTRY_DIAL_CODES.find((o) => o.dialCode === selected.dialCode);
          return parsePhoneLength(fb?.phoneLength);
        })()
      : null);
  const maxDigits = length?.max ?? 15;
  const local = localPhoneDigits(phone).slice(0, maxDigits);
  const lengthHint = length
    ? (length.min === length.max
      ? `${length.min} digits`
      : `${length.min}–${length.max} digits`)
    : undefined;
  const message = helperText || (lengthHint
    ? `Country code required. Phone must be ${lengthHint} for ${selected?.dialCode || 'this code'}`
    : 'Select a country code, then enter the phone number');

  const applyCode = (nextCode: string) => {
    const next = options.find((o) => o.dialCode === nextCode);
    const nextMax = parsePhoneLength(next?.phoneLength)?.max ?? 15;
    onChange({ countryCode: nextCode, phone: localPhoneDigits(phone).slice(0, nextMax) });
  };

  const dialAutocomplete = (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      loading={isLoading}
      value={selected}
      inputValue={codeQuery || (selected ? `${selected.dialCode}` : '')}
      onInputChange={(_, v, reason) => {
        if (reason === 'reset' && selected) {
          setCodeQuery(selected.dialCode);
          return;
        }
        setCodeQuery(v);
      }}
      onChange={(_, opt) => {
        if (!opt) return;
        applyCode(opt.dialCode);
        setCodeQuery(opt.dialCode);
      }}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(a, b) => a.dialCode === b.dialCode && a.code === b.code}
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase().replace(/\s+/g, '');
        if (!q) return opts;
        return opts.filter((o) => {
          const hay = `${o.dialCode} ${o.code} ${o.name}`.toLowerCase().replace(/\s+/g, '');
          return hay.includes(q) || o.dialCode.replace('+', '').includes(q.replace('+', ''));
        });
      }}
      disableClearable
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderOption={(props, o) => (
        <li {...props} key={`${o.code}-${o.dialCode}`}>
          <Box component="span" sx={{ fontWeight: 800, minWidth: 52 }}>{o.dialCode}</Box>
          <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>{o.code || o.name}</Box>
        </li>
      )}
      sx={layout === 'inline' ? {
        minWidth: 88,
        maxWidth: 110,
        '& .MuiOutlinedInput-root': {
          p: '0 !important',
          bgcolor: 'transparent',
          '& fieldset': { border: 'none' },
        },
        '& .MuiAutocomplete-input': { fontWeight: 800, fontSize: '0.85rem', p: '0 !important' },
      } : { minWidth: 0 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={layout === 'inline' ? undefined : 'Country code'}
          required={required || layout !== 'inline'}
          error={Boolean(error && !countryCode && !selected)}
          placeholder="+91"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  if (layout === 'inline') {
    return (
      <Box sx={{ width: '100%', minWidth: 0 }}>
        <PhoneNumberField
          fullWidth
          size="small"
          label={label}
          required={required}
          value={local}
          disabled={disabled || (isLoading && !options.length)}
          error={error}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          inputProps={{ maxLength: maxDigits }}
          onChange={(e) => onChange({
            countryCode: selected?.dialCode || countryCode || '',
            phone: localPhoneDigits(e.target.value).slice(0, maxDigits),
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.75, ml: -0.5, maxWidth: 120 }}>
                {dialAutocomplete}
              </InputAdornment>
            ),
            endAdornment: isLoading && !options.length ? <CircularProgress size={16} /> : undefined,
          }}
        />
        {!hideHelper && (
          <FormHelperText error={Boolean(error)} sx={{ mx: '14px', mt: 0 }}>
            {message}
          </FormHelperText>
        )}
      </Box>
    );
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} sm={5} md={4}>
        {dialAutocomplete}
      </Grid>
      <Grid item xs={12} sm={7} md={8}>
        <PhoneNumberField
          fullWidth
          size="small"
          label={label}
          required={required}
          value={local}
          disabled={disabled || (isLoading && !options.length)}
          error={error}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          inputProps={{ maxLength: maxDigits }}
          onChange={(e) => onChange({
            countryCode: selected?.dialCode || countryCode || '',
            phone: localPhoneDigits(e.target.value).slice(0, maxDigits),
          })}
          InputProps={{
            endAdornment: isLoading && !options.length ? <CircularProgress size={16} /> : undefined,
          }}
        />
      </Grid>
      {!hideHelper && (
        <Grid item xs={12}>
          <FormHelperText error={Boolean(error)} sx={{ mx: '14px', mt: -0.5 }}>
            {message}
          </FormHelperText>
        </Grid>
      )}
    </Grid>
  );
}
